import { Spin } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import { handleException } from 'clients/exception-handling'
import { useFilterService } from 'features/filters/service'
// bully ignore[no-cross-feature-private-imports: project-level feature-flag hooks are imported cross-feature throughout trend-detection with no public seam (agent-list/Container.tsx, agent-detail/Container.tsx, etc. carry the identical unignored import already) — same accepted debt category the filters hooks below cite. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { useFeatureFlags } from 'features/project/hooks/use-feature-flags'
// bully ignore[no-cross-feature-private-imports: same as useFeatureFlags above — the response type has no public seam either. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import type { FeatureFlagsResponse } from 'features/project/service/feature-flags-service'
import { useProjectStore } from 'features/project/store'
import { AgentSharing } from 'features/trend-detection/components/AgentSharing/AgentSharing'
import { AgentCreateForm } from 'features/trend-detection/features/agent-create/components/AgentCreateForm'
import { BenchmarkingAgentFields } from 'features/trend-detection/features/agent-create/components/BenchmarkingAgentFields'
import { IssueAgentFields } from 'features/trend-detection/features/agent-create/components/IssueAgentFields'
import { PermissionsSection } from 'features/trend-detection/features/agent-create/components/PermissionsSection'
import { ReportsAgentFields } from 'features/trend-detection/features/agent-create/components/ReportsAgentFields'
import { StaleFiltersNotice } from 'features/trend-detection/features/agent-create/components/StaleFiltersNotice/StaleFiltersNotice'
import { useAgentCreateForm } from 'features/trend-detection/features/agent-create/hooks/use-agent-create-form'
import { useAgentCreateSubmit } from 'features/trend-detection/features/agent-create/hooks/use-agent-create-submit'
import { useAgentUpdateSubmit } from 'features/trend-detection/features/agent-create/hooks/use-agent-update-submit'
import { useBenchmarkingCreateSubmit } from 'features/trend-detection/features/agent-create/hooks/use-benchmarking-create-submit'
import { useBenchmarkingSetup } from 'features/trend-detection/features/agent-create/hooks/use-benchmarking-setup'
import { useBenchmarkingUpdateSubmit } from 'features/trend-detection/features/agent-create/hooks/use-benchmarking-update-submit'
import { useEditBenchmarkingAgentQuery } from 'features/trend-detection/features/agent-create/hooks/use-edit-benchmarking-agent-query'
import { useEditIssueAgentQuery } from 'features/trend-detection/features/agent-create/hooks/use-edit-issue-agent-query'
import { useGeneralAgentCreateSubmit } from 'features/trend-detection/features/agent-create/hooks/use-general-agent-create-submit'
import { parseAgentTypeParam } from 'features/trend-detection/features/agent-create/utils/parse-agent-type-param'
import { getSubmitBlockers } from 'features/trend-detection/features/agent-create/utils/submit-blockers'
import { ISSUE_AGENTS_QUERY_KEY } from 'features/trend-detection/features/agent-list/hooks/use-agents-list'
import { useAgentInsightService } from 'features/trend-detection/features/insight-agent/service'
import {
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import { trendDetectionAgentsPath } from 'features/trend-detection/paths'
import { useTrendDetectionService } from 'features/trend-detection/service'
import type { AgentType, FilterCriterion } from 'features/trend-detection/types'
import { invalidateFeedQueries } from 'features/trend-detection/utils/invalidate-feed-queries'
import { useAgentFilterPanel } from 'src/hooks/use-agent-filter-panel'
import type { ProjectState } from 'types/project'

const SETUP_AGENT_TYPE_PARAM = 'agent_type'

const hasKeys = (record: Record<string, string>): boolean =>
  Object.keys(record).length > 0

const NAME_PLACEHOLDER: Record<AgentType, string> = {
  issue_detection: 'Name your Issue Agent...',
  benchmarking: 'Name your Benchmarking Agent...',
  general: 'Name your Reports Agent...',
}

/** Single-screen agent create/edit page. Dispatches by `agentType`; per-type
 * submit logic lives in sibling `use-*-submit` hooks. */
export const AgentCreate: React.FC = () => {
  const projectId = useProjectStore((state: ProjectState) => state.projectId)
  const service = useTrendDetectionService()
  const filterService = useFilterService()
  const insightAgentService = useAgentInsightService()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()

  const { data: featureFlagsRaw, isLoading: featureFlagsLoading } =
    useFeatureFlags()
  const featureFlags = featureFlagsRaw as FeatureFlagsResponse | undefined
  const hasBenchmarkingFlag = !!featureFlags?.['benchmarking_agent']
  const hasGeneralAgentFlag = !!featureFlags?.['general_agent']
  const emailDigestEnabled = !!featureFlags?.['issue_agent_email_digest']

  const availableAgentTypes: AgentType[] = useMemo(
    () => [
      'issue_detection',
      ...(hasBenchmarkingFlag ? (['benchmarking'] as const) : []),
      ...(hasGeneralAgentFlag ? (['general'] as const) : []),
    ],
    [hasBenchmarkingFlag, hasGeneralAgentFlag],
  )

  // Seeded from ?agent_type=... so the list page's QuickCreateCards choice
  // survives the list → create route boundary (list and create mount
  // separate Container instances).
  const [agentType, setAgentType] = useState<AgentType>(
    () =>
      parseAgentTypeParam(searchParams.get(SETUP_AGENT_TYPE_PARAM)) ??
      'issue_detection',
  )
  const listPath = trendDetectionAgentsPath(projectId ?? '')

  // Edit mode: `?agent_id=...` alongside `?agent_type=...` — set by the
  // detail pages' "Edit Agent" action instead of the old `?mode=edit` +
  // agent-config wizard. The type isn't editable once set (see the
  // disabled AgentTypeField below), so agentId alone is enough to gate on.
  const editAgentId = searchParams.get('agent_id')
  const isEditMode = !!editAgentId

  const form = useAgentCreateForm({ service, projectId })
  const { createAndRun, isCreating } = useAgentCreateSubmit({
    service,
    projectId,
    form,
  })
  const { saveAndRun: saveAndRunIssueAgent, isSaving: isSavingIssueAgent } =
    useAgentUpdateSubmit({ service, projectId, agentId: editAgentId, form })

  const { data: editIssueAgentData, isLoading: editIssueAgentLoading } =
    useEditIssueAgentQuery({
      service,
      projectId,
      agentId: editAgentId,
      enabled: isEditMode && agentType === 'issue_detection' && !!projectId,
    })

  // No `staleByField` — the notice below is the single stale-filter surface.
  // Passing it would also light up FilterCard's warning badge, duplicating the
  // signal without adding action.
  //
  // Names for uuids the suggest call just emitted layer under the saved
  // agent's own labels. A present map means "a theme uuid missing from it is
  // deleted", so an edit whose saved-label lookup failed (nullish) must stay
  // map-less rather than gain one from the suggestions alone.
  const { suggestedLabels } = form
  const savedLabels = editIssueAgentData?.filter_value_labels
  const valueLabels = useMemo(() => {
    if (!savedLabels && (editIssueAgentData || !hasKeys(suggestedLabels))) {
      return savedLabels
    }
    return { ...suggestedLabels, ...savedLabels }
  }, [editIssueAgentData, savedLabels, suggestedLabels])
  const { filterControlProps } = useAgentFilterPanel({
    projectId,
    criteria: form.criteria,
    onCriteriaChange: form.setCriteria,
    valueLabels,
  })

  // Surgical stale-value removal.
  //
  // `value === null` is FIELD-level (unknown_field / range_field_misuse):
  // every criterion on that field is broken by construction — the field
  // itself isn't in the taxonomy anymore, or is being used the wrong way —
  // so dropping all criteria matching field is correct. Cannot preserve a
  // "valid" sibling criterion here because none can be valid.
  //
  // Otherwise value-level: drop THAT stored value from criteria matching
  // field (there may be more than one criterion for the same field — LLM
  // emits theme + parent_theme separately; a user can legitimately have
  // both). Empty criteria are dropped so the picker doesn't render an empty
  // card.
  const { criteria: formCriteria, setCriteria: setFormCriteria } = form
  const handleRemoveStaleValue = useCallback(
    ({ field, value }: { field: string; value: string | null }) => {
      const nextCriteria = formCriteria
        .map((c) => {
          if (c.field !== field) return c
          if (value === null) return null
          const nextValues = (c.values ?? []).filter((v) => v !== value)
          return nextValues.length > 0 ? { ...c, values: nextValues } : null
        })
        .filter((c): c is FilterCriterion => c !== null)
      setFormCriteria(nextCriteria)
    },
    [formCriteria, setFormCriteria],
  )

  const {
    data: editBenchmarkingAgentData,
    isLoading: editBenchmarkingAgentLoading,
  } = useEditBenchmarkingAgentQuery({
    service,
    projectId,
    agentId: editAgentId,
    enabled: isEditMode && agentType === 'benchmarking' && !!projectId,
  })

  const benchmarkingSetup = useBenchmarkingSetup({
    filterService,
    projectId,
    valueLabels: editBenchmarkingAgentData?.filter_value_labels,
  })

  // Benchmarking removal — same shape as `handleRemoveStaleValue` above, but
  // dispatched across cohort AND focus lists. `field` may occur in either
  // scope (or both — themes can appear on both sides). Remove wherever
  // matched; the notice's cross-reference hides rows once their values are
  // gone.
  const {
    cohortFilters: benchmarkingCohortFilters,
    setCohortFilters: setBenchmarkingCohortFilters,
    focusGroupFilters: benchmarkingFocusFilters,
    setFocusGroupFilters: setBenchmarkingFocusFilters,
  } = benchmarkingSetup
  const handleRemoveBenchmarkingStaleValue = useCallback(
    ({ field, value }: { field: string; value: string | null }) => {
      const strip = (list: FilterCriterion[]): FilterCriterion[] =>
        list
          .map((c) => {
            if (c.field !== field) return c
            if (value === null) return null
            const nextValues = (c.values ?? []).filter((v) => v !== value)
            return nextValues.length > 0 ? { ...c, values: nextValues } : null
          })
          .filter((c): c is FilterCriterion => c !== null)
      setBenchmarkingCohortFilters(strip(benchmarkingCohortFilters))
      setBenchmarkingFocusFilters(strip(benchmarkingFocusFilters))
    },
    [
      benchmarkingCohortFilters,
      benchmarkingFocusFilters,
      setBenchmarkingCohortFilters,
      setBenchmarkingFocusFilters,
    ],
  )
  const {
    submit: submitBenchmarkingAgent,
    isSubmitting: isSubmittingBenchmarking,
  } = useBenchmarkingCreateSubmit({ projectId, service, benchmarkingSetup })
  const {
    saveAndRun: saveAndRunBenchmarkingAgent,
    isSubmitting: isSavingBenchmarkingAgent,
  } = useBenchmarkingUpdateSubmit({
    projectId,
    agentId: editAgentId,
    service,
    benchmarkingSetup,
  })

  // Hydrate the per-type form hook exactly once per agent, when its detail
  // resolves — mirrors the ref-guarded seeding BenchmarkingAgentDetailPage's
  // (deleted) edit branch used, since the fetch is async and can't be read
  // synchronously in the form hooks' own initializers.
  const hydratedForAgentRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isEditMode) return
    if (agentType === 'issue_detection' && editIssueAgentData) {
      if (hydratedForAgentRef.current === editIssueAgentData.uuid) return
      hydratedForAgentRef.current = editIssueAgentData.uuid
      form.hydrateFromAgent(editIssueAgentData)
    } else if (agentType === 'benchmarking' && editBenchmarkingAgentData) {
      if (hydratedForAgentRef.current === editBenchmarkingAgentData.uuid) return
      hydratedForAgentRef.current = editBenchmarkingAgentData.uuid
      benchmarkingSetup.hydrateFromAgent(editBenchmarkingAgentData)
    }
  }, [
    isEditMode,
    agentType,
    editIssueAgentData,
    editBenchmarkingAgentData,
    form,
    benchmarkingSetup,
  ])

  // Reports Agent (general-agent). Form lives in this Container until the
  // agent's persistence story firms up; see
  // `api/features/features/reports_agent/DESIGN.md`. Shares the unified
  // name field (`form.agentName`) — only benchmarking keeps its own name
  // state, because `useBenchmarkingSetup` owns it internally.
  const [generalAgentPrompt, setGeneralAgentPrompt] = useState('')
  const { submit: submitGeneralAgent, isSubmitting: isSubmittingGeneral } =
    useGeneralAgentCreateSubmit({
      projectId,
      service: insightAgentService,
      name: form.agentName,
      prompt: generalAgentPrompt,
    })

  const formOpenFiredRef = useRef(false)
  useEffect(() => {
    if (formOpenFiredRef.current) return
    formOpenFiredRef.current = true
    trackResearchAgents(ResearchAgentsAction.create_agent_form_open, {
      agent_type: agentType,
    })
  }, [agentType])

  const handleAgentTypeChange = useCallback((type: AgentType) => {
    trackResearchAgents(ResearchAgentsAction.agent_type_select, {
      agent_type: type,
    })
    setAgentType(type)
  }, [])

  const handleNameChange = useCallback(
    (value: string) => {
      if (agentType === 'benchmarking') benchmarkingSetup.setAgentName(value)
      else form.setAgentName(value)
    },
    [agentType, benchmarkingSetup, form],
  )

  const handleSuggestFilters = useCallback(async () => {
    trackResearchAgents(ResearchAgentsAction.agent_generate_filters_click, {
      flow: 'create',
      agent_type: agentType,
    })
    await form.suggestFilters()
  }, [agentType, form])

  const handleCancel = useCallback(() => {
    trackResearchAgents(ResearchAgentsAction.agent_create_cancel, {
      agent_type: agentType,
      step: 'form',
    })
    form.resetForm()
    benchmarkingSetup.reset()
    setGeneralAgentPrompt('')
    navigate(listPath, { replace: true })
  }, [agentType, form, benchmarkingSetup, navigate, listPath])

  const handleCreateIssueAgent = useCallback(async () => {
    try {
      const { agent, runOutcome, emailPreferenceSaved } = await createAndRun()
      await invalidateFeedQueries(queryClient, projectId!)
      queryClient.invalidateQueries([ISSUE_AGENTS_QUERY_KEY, projectId])
      trackResearchAgents(ResearchAgentsAction.agent_create, {
        agent_type: agent.agent_type,
        agent_uuid: agent.uuid,
        agent_name: agent.name,
      })
      if (runOutcome.status === 'succeeded') {
        trackResearchAgents(ResearchAgentsAction.agent_run, {
          agent_type: agent.agent_type,
          agent_uuid: agent.uuid,
          agent_name: agent.name,
          trigger: 'create',
        })
        toast.success('Agent created successfully!')
      } else {
        toast.warn(
          'Agent created, but its run did not start. Open the agent to run it again.',
        )
      }
      if (!emailPreferenceSaved)
        toast.warn(
          'Agent created, but its email preference was not saved. Update it on the agent page.',
        )
      form.resetForm()
      navigate(listPath, { replace: true })
    } catch (err) {
      handleException(err, {
        operation: 'agent.issue.create',
        disposition: 'failed',
      })

      const message =
        err instanceof Error ? err.message : 'Failed to create agent'
      toast.error(message)
    }
  }, [createAndRun, queryClient, projectId, form, navigate, listPath])

  const handleUpdateIssueAgent = useCallback(async () => {
    try {
      const agent = await saveAndRunIssueAgent()
      await invalidateFeedQueries(queryClient, projectId!)
      queryClient.invalidateQueries([ISSUE_AGENTS_QUERY_KEY, projectId])
      queryClient.invalidateQueries(['trend-agent', projectId, editAgentId])
      trackResearchAgents(ResearchAgentsAction.agent_edit_save, {
        agent_type: agent.agent_type,
        agent_uuid: agent.uuid,
        agent_name: agent.name,
      })
      toast.success('Agent saved; run started.')
      navigate(listPath, { replace: true })
    } catch (err) {
      handleException(err, {
        operation: 'agent.issue.update',
        disposition: 'failed',
      })

      const message =
        err instanceof Error ? err.message : 'Failed to save agent'
      toast.error(message)
    }
  }, [
    saveAndRunIssueAgent,
    queryClient,
    projectId,
    editAgentId,
    navigate,
    listPath,
  ])

  const handleSubmit = useCallback(() => {
    if (agentType === 'benchmarking') {
      if (isEditMode) {
        void saveAndRunBenchmarkingAgent().then((succeeded) => {
          if (succeeded) navigate(listPath, { replace: true })
        })
        return
      }
      void submitBenchmarkingAgent().then((succeeded) => {
        if (succeeded) {
          benchmarkingSetup.reset()
          navigate(listPath, { replace: true })
        }
      })
    } else if (agentType === 'general') {
      void submitGeneralAgent().then((succeeded) => {
        if (succeeded) {
          form.resetForm()
          setGeneralAgentPrompt('')
          navigate(listPath, { replace: true })
        }
      })
    } else if (isEditMode) {
      void handleUpdateIssueAgent()
    } else {
      void handleCreateIssueAgent()
    }
  }, [
    agentType,
    isEditMode,
    saveAndRunBenchmarkingAgent,
    submitBenchmarkingAgent,
    benchmarkingSetup,
    submitGeneralAgent,
    form,
    handleUpdateIssueAgent,
    handleCreateIssueAgent,
    navigate,
    listPath,
  ])

  if (!projectId) return null

  // While editing, hold the form until the existing agent's detail has
  // resolved — submitting against un-hydrated defaults would PATCH the
  // agent back to its create-form placeholder state.
  const isLoadingEditAgent =
    isEditMode &&
    (agentType === 'benchmarking'
      ? editBenchmarkingAgentLoading || !editBenchmarkingAgentData
      : editIssueAgentLoading || !editIssueAgentData)
  if (isLoadingEditAgent) return <FormLoadingState />

  const submitting = isEditMode
    ? agentType === 'benchmarking'
      ? isSavingBenchmarkingAgent
      : isSavingIssueAgent
    : agentType === 'benchmarking'
      ? isSubmittingBenchmarking
      : agentType === 'general'
        ? isSubmittingGeneral
        : isCreating

  const nameValue =
    agentType === 'benchmarking' ? benchmarkingSetup.agentName : form.agentName

  const blockers = getSubmitBlockers({
    agentType,
    isEditMode,
    name: nameValue,
    issuePrompt: form.prompt,
    generalPrompt: generalAgentPrompt,
    benchmarkingEntityType: benchmarkingSetup.entityType,
    benchmarkingFocusCount: benchmarkingSetup.focusGroupFilters.length,
  })
  const submitDisabled =
    blockers.length > 0 ||
    (agentType === 'benchmarking' &&
      benchmarkingSetup.focusGroupFilterOptionsLoading)

  let instructions: React.ReactNode
  if (agentType === 'benchmarking') {
    instructions = (
      <BenchmarkingAgentFields
        fields={{
          entityType: benchmarkingSetup.entityType,
          onEntityTypeChange: benchmarkingSetup.setEntityType,
          filterControlProps: benchmarkingSetup.filterControlProps,
          cohortFilters: benchmarkingSetup.cohortFilters,
          focusGroupFilters: benchmarkingSetup.focusGroupFilters,
          onFocusGroupFiltersChange: benchmarkingSetup.setFocusGroupFilters,
          projectId,
          focusGroupFilterOptions: benchmarkingSetup.focusGroupFilterOptions,
          focusGroupFilterOptionsLoading:
            benchmarkingSetup.focusGroupFilterOptionsLoading,
          // Only wire during edit — creation has no saved config to check.
          staleFilters: isEditMode
            ? (editBenchmarkingAgentData?.stale_filters ?? [])
            : undefined,
          isEditor: editBenchmarkingAgentData?.is_editor ?? false,
          onRemoveStaleValue: handleRemoveBenchmarkingStaleValue,
        }}
      />
    )
  } else if (agentType === 'general') {
    instructions = (
      <ReportsAgentFields
        prompt={generalAgentPrompt}
        onPromptChange={setGeneralAgentPrompt}
      />
    )
  } else {
    // Notice rendered INSIDE IssueAgentFields, directly above the Filters
    // section, rather than at the top of Instructions — the picker IS the
    // action, so a notice far from it is a wasted trip. See
    // `IssueAgentFields`'s `staleFiltersNotice` slot.
    instructions = (
      <IssueAgentFields
        prompt={form.prompt}
        onPromptChange={form.setPrompt}
        onSuggestFilters={handleSuggestFilters}
        generatingFilters={form.generatingFilters}
        sensitivity={form.sensitivity}
        onSensitivityChange={form.setSensitivity}
        filterPanel={{
          filterControlProps,
          staleFiltersNotice: isEditMode ? (
            <StaleFiltersNotice
              staleFilters={editIssueAgentData?.stale_filters ?? []}
              criteria={form.criteria}
              isEditor={editIssueAgentData?.is_editor ?? false}
              onRemove={handleRemoveStaleValue}
            />
          ) : null,
        }}
      />
    )
  }

  let permissions: React.ReactNode
  if (agentType === 'benchmarking') {
    permissions = (
      <PermissionsSection
        emailNotifications={benchmarkingSetup.emailNotifications}
        onEmailNotificationsChange={benchmarkingSetup.setEmailNotifications}
      />
    )
  } else if (agentType === 'issue_detection' && emailDigestEnabled) {
    permissions = (
      <PermissionsSection
        emailNotifications={form.emailNotifications}
        onEmailNotificationsChange={form.setEmailNotifications}
      />
    )
  }

  // Sharing only applies to an agent that already exists — never rendered
  // on create, and never for a `default` agent (nobody can share those).
  let sharing: React.ReactNode
  if (isEditMode) {
    if (agentType === 'benchmarking' && editBenchmarkingAgentData) {
      const agent = editBenchmarkingAgentData
      if (!agent.is_default) {
        sharing = (
          <AgentSharing
            agentUuid={agent.uuid}
            projectId={projectId}
            isEditor={agent.is_editor}
            agentType="benchmarking"
          />
        )
      }
    } else if (agentType === 'issue_detection' && editIssueAgentData) {
      const agent = editIssueAgentData
      if (!agent.is_default) {
        sharing = (
          <AgentSharing
            agentUuid={agent.uuid}
            projectId={projectId}
            isEditor={agent.is_editor}
            agentType="issue_detection"
          />
        )
      }
    }
  }

  return (
    <AgentCreateForm
      header={{
        name: nameValue,
        onNameChange: handleNameChange,
        namePlaceholder: NAME_PLACEHOLDER[agentType],
        agentType,
        onAgentTypeChange: handleAgentTypeChange,
        availableAgentTypes,
        typeDisabled: isEditMode,
      }}
      instructions={instructions}
      permissions={permissions}
      sharing={sharing}
      submit={{
        label: isEditMode ? 'Save and run' : 'Create and run',
        submittingLabel: isEditMode ? 'Saving…' : 'Creating…',
        disabled: submitDisabled || featureFlagsLoading,
        blockers,
        submitting,
        onSubmit: handleSubmit,
      }}
      onCancel={handleCancel}
    />
  )
}

const FormLoadingState: React.FC = () => (
  <LoadingCentered>
    <Spin size="large" />
  </LoadingCentered>
)

const LoadingCentered = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 320px;
`
